type Node = { id: string; tier: number; column: number; prerequisites?: string[] };
export function layoutSkillNodes(skills: Node[]): Map<string, { row: number; col: number }> {
  const sorted = [...skills].sort(
    (a, b) => a.tier - b.tier || a.column - b.column || a.id.localeCompare(b.id),
  );
  const result = new Map(sorted.map((n) => [n.id, { row: n.tier, col: n.column }]));
  const tiers = [...new Set(sorted.map((n) => n.tier))];
  const edges = sorted.flatMap((n) =>
    (n.prerequisites ?? []).filter((id) => result.has(id)).map((id) => ({ from: id, to: n.id })),
  );
  // Each layer has at most five nodes: exhaustively assign its columns instead
  // of greedy placement, then sweep both directions to reduce edge crossings.
  for (let sweep = 0; sweep < 6; sweep++)
    for (const tier of sweep % 2 ? [...tiers].reverse() : tiers) {
      const layer = sorted.filter((n) => n.tier === tier);
      let best = Infinity;
      let chosen: number[] = [];
      const assign = (cols: number[]) => {
        if (cols.length < layer.length) {
          for (let c = 1; c <= 5; c++) if (!cols.includes(c)) assign([...cols, c]);
          return;
        }
        const column = (id: string) => {
          const i = layer.findIndex((n) => n.id === id);
          return i < 0 ? result.get(id)!.col : cols[i];
        };
        let cost = layer.reduce((v, n, i) => v + Math.abs(cols[i] - n.column) * 0.01, 0);
        for (const e of edges) cost += Math.abs(column(e.from) - column(e.to));
        for (let i = 0; i < edges.length; i++)
          for (let j = i + 1; j < edges.length; j++) {
            const a = edges[i],
              b = edges[j];
            if (a.from === b.from || a.to === b.to) continue;
            if (
              result.get(a.from)!.row === result.get(b.from)!.row &&
              result.get(a.to)!.row === result.get(b.to)!.row &&
              (column(a.from) - column(b.from)) * (column(a.to) - column(b.to)) < 0
            )
              cost += 100;
          }
        if (cost < best) {
          best = cost;
          chosen = cols;
        }
      };
      assign([]);
      layer.forEach((n, i) => result.set(n.id, { row: tier, col: chosen[i] }));
    }
  return result;
}

export function skillDependencyPath(
  parent: { row: number; col: number },
  child: { row: number; col: number },
  index: number,
): string {
  const x1 = (parent.col - 0.5) * 100,
    y1 = (parent.row - 0.5) * 100,
    x2 = (child.col - 0.5) * 100,
    y2 = (child.row - 0.5) * 100;
  const offset = (index % 3) * 3,
    exit = y1 + 40 + offset,
    entry = y2 - 40 - offset;
  if (child.row === parent.row + 1) return `M ${x1} ${y1} V ${exit} H ${x2} V ${y2}`;
  // Long edges travel in column gutters, never through intermediate nodes.
  const gutter = child.col >= parent.col ? parent.col * 100 : (parent.col - 1) * 100;
  return `M ${x1} ${y1} V ${exit} H ${gutter} V ${entry} H ${x2} V ${y2}`;
}
